import { httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../utils/firebase/firebase';
import { BASE_URL } from '../constants/AppUrl';
import { fixEmojiCorruption } from '../utils/encodingUtils';

const pendingStudentDataRequests = new Map();

export const fetchAllStudents = async (uid, typeOfUser) => {
  try {
    const getAllStudents = httpsCallable(CloudFunctions, 'allStudents');
    const response = await getAllStudents({
      uid,
      typeOfUser,
    });
    if (response.data.status) {
      return response.data;
    } else {
      return response;
    }
  } catch (e) {
    console.log('Error in fetchAllStudents API: ', e);
    throw e;
  }
};

export const fetchStudentProfile = async (studentId) => {
  try {
    const getStudentProfile = httpsCallable(CloudFunctions, 'getStudentProfile');
    const response = await getStudentProfile({ uid: studentId });

    if (response.data?.status) {

      const data = response.data.data || {};
      if (data && data.myDeatils && !data.myDetails) {
        data.myDetails = data.myDeatils;
      }
      return data.myDetails || data.myDeatils || null;
    } else {
      console.warn('fetchStudentProfile: API returned false status', response.data?.message);
      return null;
    }
  } catch (error) {
    console.error('fetchStudentProfile: Error fetching student profile', error);
    throw error;
  }
};

export const updateStudentTreatmentDetails = async (
  uid,
  studentId,
  treatmentId,
  treatment
) => {
  try {
    const updateStudentTreatmentInfo = httpsCallable(
      CloudFunctions,
      'updateStudentTreatmentInfo'
    );
    const response = await updateStudentTreatmentInfo({
      uid,
      studentId,
      treatmentId,
      treatment,
    });
    if (response.data.status) {
      return response.data.message;
    } else {
      return null;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const fetchStudentData = async (uid, typeOfUser, options = {}) => {
  try {
    // Create a unique key for this request
    const requestKey = `${uid}-${typeOfUser}-${options.includeDataCollection}-${options.includeGoalData}`;

    // If request is already in progress, return the existing promise
    if (pendingStudentDataRequests.has(requestKey)) {
      return pendingStudentDataRequests.get(requestKey);
    }

    // Create the request promise
    const requestPromise = (async () => {
      const getStudentData = httpsCallable(CloudFunctions, 'getStudentData');

      const response = await getStudentData({
        uid,
        typeOfUser,
        includeDataCollection: options.includeDataCollection ?? true,
        includeGoalData: options.includeGoalData ?? true,
      });

      if (response.data?.status) {
        const studentData = response.data.studentData;

        // Fix emoji corruption in goal data
        if (studentData && Array.isArray(studentData)) {
          const fixedStudentData = studentData.map((student) => {
            if (
              student.dataCollection &&
              Array.isArray(student.dataCollection)
            ) {
              const fixedDataCollection = student.dataCollection.map(
                (collection) => {
                  if (collection.info) {
                    collection.info = fixEmojiCorruption(collection.info);
                  }
                  if (
                    collection.goalData &&
                    Array.isArray(collection.goalData)
                  ) {
                    collection.goalData = collection.goalData.map((goal) => {
                      if (goal.info) {
                        goal.info = fixEmojiCorruption(goal.info);
                      }
                      return goal;
                    });
                  }
                  return collection;
                }
              );
              student.dataCollection = fixedDataCollection;
            }
            return student;
          });

          return fixedStudentData;
        }

        return studentData;
      } else {
        console.warn(
          'fetchStudentData: API returned false status',
          response.data.message
        );
        return null;
      }
    })();

    // Store the promise and clean up when done
    pendingStudentDataRequests.set(requestKey, requestPromise);
    requestPromise.finally(() => pendingStudentDataRequests.delete(requestKey));

    return requestPromise;
  } catch (error) {
    console.error('fetchStudentData: Error fetching student data', error);
    return null;
  }
};

export const fetchAllStudentDataForSchoolAdmin = async (uid, options = {}) => {
  try {
    // Create a unique key for this request
    const requestKey = `${uid}-schoolAdmin-${options.includeDataCollection}-${options.includeGoalData}`;

    // If request is already in progress, return the existing promise
    if (pendingStudentDataRequests.has(requestKey)) {
      return pendingStudentDataRequests.get(requestKey);
    }

    // Create the request promise
    const requestPromise = (async () => {
      const getAllStudentDataForSchoolAdmin = httpsCallable(
        CloudFunctions,
        'getAllStudentDataForSchoolAdmin'
      );

      const response = await getAllStudentDataForSchoolAdmin({
        uid,
        includeDataCollection: options.includeDataCollection ?? true,
        includeGoalData: options.includeGoalData ?? true,
      });

      if (response.data?.status) {
        const studentData = response.data.studentData;

        // Fix emoji corruption in goal data
        if (studentData && Array.isArray(studentData)) {
          const fixedStudentData = studentData.map((student) => {
            if (
              student.dataCollection &&
              Array.isArray(student.dataCollection)
            ) {
              const fixedDataCollection = student.dataCollection.map(
                (collection) => {
                  if (collection.info) {
                    collection.info = fixEmojiCorruption(collection.info);
                  }
                  if (
                    collection.goalData &&
                    Array.isArray(collection.goalData)
                  ) {
                    collection.goalData = collection.goalData.map((goal) => {
                      if (goal.info) {
                        goal.info = fixEmojiCorruption(goal.info);
                      }
                      return goal;
                    });
                  }
                  return collection;
                }
              );
              student.dataCollection = fixedDataCollection;
            }
            return student;
          });

          return fixedStudentData;
        }

        return studentData;
      } else {
        console.warn(
          'fetchAllStudentDataForSchoolAdmin: API returned false status',
          response.data.message
        );
        return null;
      }
    })();

    // Store the promise and clean up when done
    pendingStudentDataRequests.set(requestKey, requestPromise);
    requestPromise.finally(() => pendingStudentDataRequests.delete(requestKey));

    return requestPromise;
  } catch (error) {
    console.error(
      'fetchAllStudentDataForSchoolAdmin: Error fetching student data',
      error
    );
    return null;
  }
};

export const fetchPreRegisteredUsers = async (email) => {
  try {
    const fetchPreRegisteredUsersCallable = httpsCallable(
      CloudFunctions,
      'fetchPreRegisteredUsers'
    );
    const response = await fetchPreRegisteredUsersCallable({ email });
    if (response?.data?.status) {
      if (Array.isArray(response?.data?.user)) {
        return response.data.user;
      } else if (response?.data?.user) {
        return [response.data.user];
      } else {
        return [];
      }
    } else {
      return [];
    }
  } catch (e) {
    console.error('Error in fetchPreRegisteredUsers:', e);
    // throw e;
    return [];
  }
};

export const fetchAllPreRegisterUser = async (uid, typeOfUser) => {
  try {
    const getAllPreRegisterUser = httpsCallable(
      CloudFunctions,
      'allPreRegisterUser'
    );
    const response = await getAllPreRegisterUser({
      uid,
      typeOfUser,
    });
    if (response.data.status) {
      return response.data;
    } else {
      return response;
    }
  } catch (e) {
    console.log('Error in fetch All Pre-Register User API: ', e);
    throw e;
  }
};

export const fetchBehaviorData = async (uid, typeOfUser) => {
  try {
    const response = await fetch(`${BASE_URL}/getBehaviorData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uid, typeOfUser }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn('getBehaviorData: API returned HTTP error', {
        status: response.status,
        statusText: response.statusText,
        errorData,
        uid,
        typeOfUser,
      });
      if (response.status === 404) {
        return [];
      }
      return [];
    }

    const data = await response.json();

    if (data?.status) {
      // Ensure studentData is an array
      let studentData = Array.isArray(data.studentData) ? data.studentData : (data.studentData ? [data.studentData] : []);

      if (typeOfUser === 'STUDENT' || typeOfUser === 'PATIENT') {
        if (studentData.length > 0) {
          const firstItem = studentData[0];

          // Check if data is in flat array format (array of behaviors) or nested format
          if (firstItem?.docId && firstItem?.info && !firstItem?.behaviors) {
            // Flat array format - array of behavior objects directly
            // Transform to nested format matching teacher structure
            studentData = [
              {
                id: uid,
                uid: uid,
                behaviors: studentData.map((behavior) => ({
                  id: behavior.docId || behavior.id,
                  docId: behavior.docId,
                  ...behavior,
                })),
              },
            ];
          } else if (!firstItem?.behaviors && !firstItem?.id) {
            // Check if the item is actually a response object with nested studentData
            if (firstItem?.status && firstItem?.studentData && Array.isArray(firstItem.studentData)) {
              // This is a response object, extract the studentData array
              // Check if studentData is in flat array format (array of behaviors)
              const actualBehaviors = firstItem.studentData;
              const firstBehavior = actualBehaviors[0];
              
              if (firstBehavior?.docId && firstBehavior?.info && !firstBehavior?.behaviors) {
                // Flat array format - transform to nested format
                studentData = [
                  {
                    id: uid,
                    uid: uid,
                    behaviors: actualBehaviors.map((behavior) => ({
                      id: behavior.docId || behavior.id,
                      docId: behavior.docId,
                      ...behavior,
                    })),
                  },
                ];
              } else {
                // Already in nested format or unknown format
                studentData = [
                  {
                    id: uid,
                    uid: uid,
                    behaviors: actualBehaviors,
                  },
                ];
              }
            } else {
              // Check if studentData array contains response objects
              const hasNestedResponses = studentData.some(item => item?.status && item?.studentData);
              
              if (hasNestedResponses) {
                // Extract behaviors from nested response objects
                const allBehaviors = studentData.flatMap((item) => {
                  if (item?.status && item?.studentData && Array.isArray(item.studentData)) {
                    return item.studentData;
                  }
                  return item;
                });
                
                studentData = [
                  {
                    id: uid,
                    uid: uid,
                    behaviors: allBehaviors.map((behavior) => ({
                      id: behavior.docId || behavior.id,
                      docId: behavior.docId,
                      ...behavior,
                    })),
                  },
                ];
              } else {
                // Data might be in a different format - try to wrap it
                studentData = [
                  {
                    id: uid,
                    uid: uid,
                    fullname: firstItem?.fullname || firstItem?.name,
                    name: firstItem?.name || firstItem?.fullname,
                    behaviors: studentData.map((behavior) => {
                      // Ensure behavior has the correct structure
                      if (behavior.docId && behavior.info && !behavior.id) {
                        return {
                          id: behavior.docId,
                          docId: behavior.docId,
                          ...behavior,
                        };
                      }
                      return behavior;
                    }),
                  },
                ];
              }
            }
          } else if (firstItem?.behaviors && Array.isArray(firstItem.behaviors)) {
            // Already in correct format with behaviors array
            // Ensure the id matches the user uid
            if (firstItem.id !== uid) {
              studentData[0].id = uid;
              studentData[0].uid = uid;
            }
          } else {
            // Unknown format - try to wrap anyway
            studentData = [
              {
                id: uid,
                uid: uid,
                behaviors: studentData,
              },
            ];
          }
        }
      }

      return studentData;
    } else {
      console.warn('getBehaviorData: API returned false status', {
        message: data.message,
        uid,
        typeOfUser,
        fullResponse: data,
      });
      // Return empty array instead of null when status is false
      return [];
    }
  } catch (error) {
    console.error('getBehaviorData: Error fetching student data', {
      error,
      message: error.message,
      stack: error.stack,
      uid,
      typeOfUser,
    });
    return [];
  }
};

export const editStudentGoalEntry = async (
  uid,
  typeOfUser,
  studentId,
  dataCollectionId,
  goalId,
  infoValue
) => {
  try {
    const updateStudentGoalEntry = httpsCallable(
      CloudFunctions,
      'updateStudentGoalEntry'
    );
    const response = await updateStudentGoalEntry({
      uid,
      typeOfUser,
      studentId,
      dataCollectionId,
      goalId,
      infoValue,
    });
    if (response?.data?.status) {
      return response?.data?.message;
    } else {
      return null;
    }
  } catch (e) {
    console.error('Error in editStudentGoalEntry:', e);
    throw e;
  }
};

// Bulk update wrapper for frontend
export const editMultipleStudentGoalEntries = async ({
  uid,
  typeOfUser,
  studentId,
  updates, // Array of { dataCollectionId, goalId, infoValue }
}) => {
  try {
    const updateMultipleGoalEntries = httpsCallable(
      CloudFunctions,
      'updateMultipleGoalEntries'
    );

    const response = await updateMultipleGoalEntries({
      uid,
      typeOfUser,
      studentId,
      updates,
    });

    if (response?.data?.status) {
      return response?.data?.message || 'All entries updated successfully';
    } else {
      return null;
    }
  } catch (e) {
    console.error('Error in editMultipleStudentGoalEntries:', e);
    throw e;
  }
};

export const batchEditStudentGoalEntries = async (entries) => {
  try {
    const updateStudentGoalEntry = httpsCallable(
      CloudFunctions,
      'updateStudentGoalEntry'
    );

    // Backend accepts array directly
    const response = await updateStudentGoalEntry(entries);

    // Response will be an array of results
    if (Array.isArray(response?.data)) {
      const successful = response.data.filter((r) => r.status);
      const failed = response.data.filter((r) => !r.status);

      return {
        success: failed.length === 0,
        results: response.data,
        successful,
        failed,
        message: `${successful.length} entries saved successfully${failed.length > 0 ? `, ${failed.length} failed` : ''
          }`,
      };
    }

    return {
      success: false,
      message: 'Invalid response from server',
      results: [],
    };
  } catch (e) {
    console.error('Error in batchEditStudentGoalEntries:', e);
    throw e;
  }
};

export const editStudentQuickTrackEntry = async (
  uid,
  typeOfUser,
  studentId,
  behaviorId,
  quickTrackId,
  infoValue
) => {
  try {
    const response = await fetch(`${BASE_URL}/updateStudentQuickTrackEntry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uid,
        typeOfUser,
        studentId,
        behaviorId,
        quickTrackId,
        infoValue,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || 'Error updating student quick track entry'
      );
    }

    const data = await response.json();

    if (data.status) {
      return data.message;
    } else {
      return null;
    }
  } catch (e) {
    console.error('Error in editStudentQuickTrackEntry:', e);
    throw e;
  }
};

export const updateGoalDataCollection = async (
  uid,
  typeOfUser,
  studentId,
  dataCollectionId,
  info
) => {
  try {
    const updateDatacollection = httpsCallable(
      CloudFunctions,
      'updateDatacollectionGoalInfo'
    );

    const response = await updateDatacollection({
      uid,
      typeOfUser,
      studentId,
      dataCollectionId,
      info,
    });

    if (response?.data?.status) {
      return response?.data?.message;
    } else {
      const errorMessage = response?.data?.message || 'Failed to update goal';
      throw new Error(errorMessage);
    }
  } catch (e) {
    console.error('Error in updateGoalDataCollection:', e);
    throw e;
  }
};

export const updateQuickTrackInfo = async (
  uid,
  typeOfUser,
  studentId,
  behaviorId,
  info
) => {
  try {
    const updateQuickTrack = httpsCallable(
      CloudFunctions,
      'updateQuickTrackInfo'
    );

    const response = await updateQuickTrack({
      uid,
      typeOfUser,
      studentId,
      behaviorId,
      info,
    });

    if (response?.data?.status) {
      return response?.data?.message;
    } else {
      return null;
    }
  } catch (e) {
    console.error('Error in updateQuickTrackInfo:', e);
    throw e;
  }
};

export const fetchAllStudentClassRooms = async (uid) => {
  try {
    const getStudentAllClassRooms = httpsCallable(
      CloudFunctions,
      'fetchingStudentClassroms'
    );
    const response = await getStudentAllClassRooms({
      uid,
    });
    if (response.data.status) {
      return response.data;
    } else {
      return response;
    }
  } catch (e) {
    console.log('Error in fetch All Student Classroom  API: ', e);
    throw e;
  }
};

export const movePreRegisteredUserToRealUser = async (oldUid, newUid) => {
  try {
    const migrateUser = httpsCallable(
      CloudFunctions,
      'movePreRegisteredUserToRealUser'
    );

    const response = await migrateUser({ oldUid, newUid });

    if (response?.data?.status) {
      return response?.data?.message;
    } else {
      return null;
    }
  } catch (e) {
    console.error('Error in movePreRegisteredUserToRealUser:', e);
    throw e;
  }
};
